import { useState } from 'react'
import { ChevronDown, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useGamePhase } from '@/hooks/useGamePhase'
import { useAppSelector } from '@/redux/hooks'
import ChatDialogueBox from './ChatDialogueBox'
import { ChatMode } from '@/constants/chat'
import {
  NegotiationMember,
  NegotiationType,
} from '@/redux/types/negotiationWebsocket'
import { Negotiation } from '@/redux/types/room'
import { useEffectiveUser } from '@/hooks/useEffectiveUser'

const RoundDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedRound, setSelectedRound] = useState<number | null>(null)
  const [hoveredRound, setHoveredRound] = useState<number | null>(null)
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null)
  const [chatOpen, setChatOpen] = useState(false)
  const [selectedNegotiation, setSelectedNegotiation] =
    useState<Negotiation | null>(null)

  const { currentRound } = useGamePhase()
  const { currentRoom } = useAppSelector((state) => state.room)
  const { user } = useEffectiveUser()

  const availableRounds = Array.from(
    { length: currentRound - 1 },
    (_, i) => i + 1
  )

  const getInviter = (negotiation: Negotiation): string => {
    const inviter = negotiation.members.find(
      (member: NegotiationMember) => member.is_inviter
    )
    return inviter?.email || ''
  }

  const getInvitee = (negotiation: Negotiation): string => {
    const invitee = negotiation.members.find((member) => !member.is_inviter)
    return invitee?.email || ''
  }

  const getNegotiationStatus = (negotiation: Negotiation): string => {
    const hasRejected = negotiation.members.some((m) => m.status === 'REJECTED')
    const allAccepted = negotiation.members.every(
      (m) => m.status === 'ACCEPTED' || m.status === 'OWNER'
    )
    const hasPending = negotiation.members.some((m) => m.status === 'PENDING')

    if (hasRejected) return 'rejected'
    if (allAccepted) return 'accepted'
    if (hasPending) return 'pending'
    return 'unknown'
  }

  const getParticipantNames = (negotiation: Negotiation): string => {
    const inviter = getInviter(negotiation)
    const invitee = getInvitee(negotiation)
    const inviterPlayer = getPlayerNumber(inviter)
    const inviteePlayer = getPlayerNumber(invitee)
    return `Player ${inviterPlayer} ↔ Player ${inviteePlayer}`
  }

  const getNegotiationsForRound = (roundNumber: number): Negotiation[] => {
    if (!currentRoom?.negotiations) return []

    return currentRoom.negotiations.filter((negotiation: Negotiation) => {
      if (negotiation.negotiation_type === NegotiationType.PUBLIC_ROOM) {
        return false
      }

      const isUserMember = negotiation.members.some(
        (member) => member.email === user?.email
      )
      if (!isUserMember) {
        return false
      }

      return negotiation.round_number === roundNumber
    })
  }

  const getPlayerNumber = (email: string): string => {
    const match = email.match(/player(\d+)@/)
    return match ? match[1] : email.split('@')[0]
  }

  const handleMouseEnter = (roundNumber: number) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout)
      setHoverTimeout(null)
    }
    setHoveredRound(roundNumber)
  }

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setHoveredRound(null)
    }, 1000)
    setHoverTimeout(timeout)
  }

  const handleSelect = (roundNumber: number) => {
    setSelectedRound(roundNumber)
    setIsOpen(false)
  }

  const handleNegotiationClick = (negotiation: Negotiation) => {
    setSelectedNegotiation(negotiation)
    setChatOpen(true)
    setIsOpen(false)
  }

  const handleChatClose = () => {
    setChatOpen(false)
    setSelectedNegotiation(null)
  }

  const NegotiationTooltip: React.FC<{ negotiations: Negotiation[] }> = ({
    negotiations,
  }) => {
    if (negotiations.length === 0) {
      return (
        <div className='absolute left-full ml-2 top-0 bg-gray-800 text-white p-2 rounded shadow-lg z-50 whitespace-nowrap'>
          No negotiations this round
        </div>
      )
    }

    return (
      <div className='absolute left-full ml-1 top-0 bg-gray-800 text-white p-2 rounded shadow-lg z-50 min-w-[200px]'>
        <div className='text-xs font-semibold mb-1'>Negotiations:</div>
        {negotiations.map((negotiation) => {
          const inviter = getInviter(negotiation)
          const invitee = getInvitee(negotiation)
          const status = getNegotiationStatus(negotiation)

          return (
            <div
              key={negotiation.id}
              className='text-xs mb-1 flex items-center gap-1 cursor-pointer hover:bg-gray-700 rounded p-1'
              onClick={() => handleNegotiationClick(negotiation)}
            >
              <Users className='w-3 h-3' />
              <span>
                Player {getPlayerNumber(inviter)} ↔ Player{' '}
                {getPlayerNumber(invitee)}
              </span>
              <span
                className={`ml-1 px-1 rounded text-[10px] ${
                  status === 'accepted'
                    ? 'bg-green-600'
                    : status === 'pending'
                      ? 'bg-yellow-600'
                      : 'bg-red-600'
                }`}
              >
                {status}
              </span>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <>
      <div className='h-full w-[9.5rem]'>
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild>
            <Button
              className={`h-full w-full bg-[#E6ECF2] text-black hover:bg-dark-blue hover:text-white shadow-none border border-black justify-between rounded-[7px]`}
              role='combobox'
              aria-expanded={isOpen}
            >
              <span className='flex items-center gap-2'>
                {selectedRound ? `Round ${selectedRound}` : 'Select Round'}
              </span>
              <ChevronDown className='w-4 h-4 opacity-50 shrink-0' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className='w-[10rem] p-1 relative overflow-visible'>
            <div className='flex flex-col'>
              {availableRounds.length > 0 ? (
                availableRounds.map((roundNumber) => {
                  const roundNegotiations = getNegotiationsForRound(roundNumber)
                  return (
                    <div
                      key={roundNumber}
                      className='relative'
                      onMouseEnter={() => handleMouseEnter(roundNumber)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <button
                        onClick={() => handleSelect(roundNumber)}
                        className='flex items-center justify-between px-2 py-2 text-xs transition-colors cursor-pointer h-7 hover:bg-gray-100 w-full text-left'
                      >
                        <span>Round {roundNumber}</span>
                        {roundNegotiations.length > 0 && (
                          <div className='flex items-center gap-1'>
                            <Users className='w-3 h-3 text-blue-600' />
                            <span className='text-[10px] bg-blue-100 text-blue-800 px-1 rounded'>
                              {roundNegotiations.length}
                            </span>
                          </div>
                        )}
                      </button>
                      {hoveredRound === roundNumber && (
                        <div
                          onMouseEnter={() => handleMouseEnter(roundNumber)}
                          onMouseLeave={handleMouseLeave}
                        >
                          <NegotiationTooltip
                            negotiations={roundNegotiations}
                          />
                        </div>
                      )}
                    </div>
                  )
                })
              ) : (
                <span className='p-2 text-xs text-center text-gray-500'>
                  No rounds available
                </span>
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ChatDialogueBox
        isOpen={chatOpen}
        onClose={handleChatClose}
        negotiationId={selectedNegotiation?.id}
        participantNames={
          selectedNegotiation ? getParticipantNames(selectedNegotiation) : ''
        }
        mode={ChatMode.NEGOTIATION_HISTORY}
      />
    </>
  )
}

export default RoundDropdown
